import { FormattedMessage } from "react-intl";
import approveIcon from "../../../assets/buttons/approve.svg";
import downloadIcon from "../../../assets/buttons/download.svg";
import archiveIcon from "../../../assets/buttons/archive.svg";
import notesIcon from "../../../assets/ecgs/notes.svg";
import shareIcon from "../../../assets/buttons/send_mail.svg";
import useResponsive from "../../../hooks/useResponsive";
import { Button, Popover } from "@shltelemedicine/shl-ui-lib";
import DangerIcon from "../../Shared/DangerIcon/DangerIcon";
import SaveButton from "../../Shared/SaveButton/SaveButton";
import styles from "./EcgDetailsFooter.module.css";
import InfoIcon from "../../Shared/InfoIcon/InfoIcon";
import { CanRequestEcrOnDemandDto } from "../../../api/index.defs";
import { mapEcrOnDemandRejectReason } from "../../../utils/ecgUtil";
import { useCallback } from "react";
import { EcrOnDemandDataStates } from "../../../models/ecg.interface";

type EcgDetailsFooterProps = {
  mode: "save" | "approve";
  disabled?: boolean;
  saveDisabled?: boolean;
  saveFailed?: boolean;
  hideSave?: boolean;
  hideApprove?: boolean;
  enableNotes?: boolean;
  onSave?: () => void;
  onDownload?: () => void;
  onApprove?: () => void;
  onAddNote?: () => void;
  onArchive?: () => void;
  onShare?: () => void;
  onEcrOnDemand?: () => void;
  enableArchiving?: boolean;
  hideArchiving?: boolean;
  enableSendEmail?: boolean;
  ecrOnDemandData?: CanRequestEcrOnDemandDto;
};

const EcgDetailsFooter = ({
  mode = "save",
  disabled,
  saveDisabled,
  saveFailed,
  hideSave,
  hideApprove,
  enableNotes,
  onSave,
  onDownload,
  onApprove,
  onAddNote,
  onArchive,
  onShare,
  onEcrOnDemand,
  enableArchiving,
  hideArchiving,
  enableSendEmail,
  ecrOnDemandData,
}: EcgDetailsFooterProps) => {
  const { isMobile } = useResponsive();

  const onEcrOnDemandHandler = () => {
    onEcrOnDemand && onEcrOnDemand();
  };

  const displayEcrOnDemandButton =
    ecrOnDemandData &&
    ecrOnDemandData.state !== EcrOnDemandDataStates.NotAllowed;

  const isLeftPaneHidden = document
    .querySelector("[class*=SplitContainer_pane]")
    ?.getAttribute("style")
    ?.includes("width: 0%;");

  const getButtonSizeClass = useCallback((): string => {
    if (displayEcrOnDemandButton) {
      return isLeftPaneHidden
        ? styles.reducedFontNoLeftPane
        : styles.reducedFont;
    }
    return "";
  }, [isLeftPaneHidden, displayEcrOnDemandButton]);

  return (
    <footer className="p-2">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex">
          <Button
            disabled={disabled}
            icon={{ src: downloadIcon }}
            bgColor="transparent linear-gradient(180deg, #717689 0%, #393B45 100%) 0% 0% no-repeat padding-box"
            className={`${
              isMobile ? styles.actionButton : ""
            } d-flex justify-content-center ${getButtonSizeClass()}`}
            onClick={onDownload}
          >
            {!isMobile && (
              <span className="capitalized font-weight-bold">
                <FormattedMessage
                  id="GLOBAL.DOWNLOAD"
                  defaultMessage="Download"
                />
              </span>
            )}
          </Button>
          {enableArchiving && !hideArchiving && (
            <Button
              disabled={disabled}
              icon={{ src: archiveIcon }}
              bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
              className={`${
                isMobile ? styles.actionButton : ""
              } d-flex justify-content-center ml-2 ${getButtonSizeClass()}`}
              onClick={onArchive}
            >
              {!isMobile && (
                <span className="capitalized font-weight-bold">
                  <FormattedMessage
                    id="GLOBAL.ARCHIVE"
                    defaultMessage="Archive"
                  />
                </span>
              )}
            </Button>
          )}
          {enableNotes && (
            <Button
              disabled={disabled}
              icon={{ src: notesIcon }}
              bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
              className={`${
                isMobile ? styles.actionButton : ""
              } d-flex justify-content-center ml-2 ${getButtonSizeClass()}`}
              onClick={onAddNote}
            >
              {!isMobile && (
                <span className="capitalized font-weight-bold">
                  <FormattedMessage
                    id="ECG_DETAILS.NOTES.ADD"
                    defaultMessage="Add note"
                  />
                </span>
              )}
            </Button>
          )}
          {enableSendEmail && (
            <Button
              disabled={disabled}
              icon={{ src: shareIcon, width: 18, height: 18 }}
              bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
              className={`${
                isMobile ? styles.actionButton : ""
              } d-flex justify-content-center ml-2 ${getButtonSizeClass()}`}
              onClick={onShare}
            >
              {!isMobile && (
                <span className="capitalized font-weight-bold">
                  <FormattedMessage id="GLOBAL.EMAIL" defaultMessage="Email" />
                </span>
              )}
            </Button>
          )}
          {displayEcrOnDemandButton && (
            <Button
              style={{ padding: "0 15px", opacity: "1" }}
              disabled={
                disabled ||
                ecrOnDemandData?.state === EcrOnDemandDataStates.Disabled
              }
              bgColor={
                disabled ||
                ecrOnDemandData?.state === EcrOnDemandDataStates.Disabled
                  ? "#B5B5B5"
                  : "transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
              }
              className={`${
                isMobile ? styles.actionButton : ""
              } d-flex justify-content-center ml-2 ${getButtonSizeClass()}`}
              onClick={onEcrOnDemandHandler}
            >
              {!isMobile && (
                <span className="capitalized font-weight-bold">
                  <FormattedMessage
                    id="GLOBAL.ORDERCARDIOLOGISTREVIEW"
                    defaultMessage="Order Cardiologist Review"
                  />
                  {ecrOnDemandData?.state === EcrOnDemandDataStates.Disabled ? (
                    <Popover
                      id="rejectReason"
                      hoverContent={
                        <span>
                          {mapEcrOnDemandRejectReason(
                            ecrOnDemandData?.rejectReason!
                          )}
                        </span>
                      }
                      placement="top"
                      className={styles.popover}
                    >
                      <InfoIcon className={styles.infoIcon} />
                    </Popover>
                  ) : (
                    ""
                  )}
                </span>
              )}
            </Button>
          )}
        </div>

        {mode === "save" && !hideSave && (
          <div style={{ alignSelf: "stretch" }} className="d-flex">
            {saveFailed && <DangerIcon className="mx-2" />}

            <SaveButton
              className={`prim-bg-gradient-color mx-2 ${styles.saveButton} ${
                saveFailed ? styles.failed : ""
              } ${getButtonSizeClass()}`}
              disabled={saveDisabled || disabled}
              onClick={onSave}
            />
          </div>
        )}
        {mode === "approve" && !hideApprove && (
          <div className="d-flex">
            {saveFailed && <DangerIcon className="mx-2" />}

            <Button
              disabled={disabled}
              icon={{ src: approveIcon, width: 18, height: 18 }}
              bgColor="transparent linear-gradient(180deg, #3CC919 0%, #28B705 100%) 0% 0% no-repeat padding-box"
              onClick={onApprove}
              className={`${
                saveFailed ? styles.failed : ""
              } ${getButtonSizeClass()}`}
            >
              <span className="capitalized font-weight-bold">
                <FormattedMessage
                  id="ECG_DETAILS.REVIEWED"
                  defaultMessage="Reviewed"
                />
              </span>
            </Button>
          </div>
        )}
      </div>
    </footer>
  );
};

export default EcgDetailsFooter;
